import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Truck, DollarSign, Clock4, HandCoins, MapPin, ShieldCheck,
  Heart, Sprout, Lock, Sparkles, FileCheck2, Upload, CheckCircle2,
} from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const Item = ({ icon: Icon, title, text }) => (
  <div className="bg-white border border-stone-200 p-6 rounded-md lift">
    <div className="w-11 h-11 rounded-full bg-emerald-800/10 flex items-center justify-center text-emerald-800">
      <Icon size={20} strokeWidth={1.5} />
    </div>
    <div className="mt-5 font-serif text-xl text-stone-900">{title}</div>
    <p className="mt-2 text-sm text-stone-600 font-sans leading-relaxed">{text}</p>
  </div>
);

const Rule = ({ icon: Icon, title, text }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-10 h-10 rounded-full bg-amber-700/15 text-amber-700 flex items-center justify-center">
      <Icon size={18} strokeWidth={1.75} />
    </div>
    <div>
      <div className="font-serif text-lg text-stone-50">{title}</div>
      <p className="mt-1 text-sm text-stone-300 font-sans leading-relaxed">{text}</p>
    </div>
  </div>
);

const initialForm = {
  full_name: "",
  phone: "",
  license_number: "",
  license_state: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: "",
  license_plate: "",
  years_driving: "",
  payout_paypal: "",
  payout_cashapp: "",
  agreed_to_rules: false,
};

function DriverApplicationForm() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (user?.role === "driver") {
      api.get("/drivers/apply/me").then(({ data }) => {
        if (data && Object.keys(data).length) {
          setExisting(data);
          setForm({ ...initialForm, ...data, agreed_to_rules: true });
        }
      }).catch(() => {});
    }
  }, [user?.role]);

  const isDriver = user?.role === "driver";
  const isVerified = !!user?.verified;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed_to_rules) {
      toast.error("Please agree to the Azure Gypsy Rules of Engagement.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/drivers/apply", form);
      toast.success("Application submitted. We'll review and email you.");
      setExisting({ ...form, status: "submitted" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/uploads/verification", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("ID uploaded. Your account is verified.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Not signed in
  if (!user) {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-md p-10 text-center">
        <Lock className="mx-auto text-emerald-800" size={28} strokeWidth={1.5} />
        <h3 className="mt-4 font-serif text-2xl text-stone-900">Sign in to apply</h3>
        <p className="mt-2 text-sm text-stone-600 font-sans max-w-md mx-auto">
          To submit your driver application and upload your driver's license, please sign in with Google and pick "Driver" as your role.
        </p>
        <Link to="/login">
          <Button data-testid="drivers-app-signin" className="mt-6 bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">
            Sign In to Apply
          </Button>
        </Link>
      </div>
    );
  }

  // Signed in but wrong role
  if (!isDriver) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-8">
        <h3 className="font-serif text-2xl text-stone-900">Switch to the Driver role</h3>
        <p className="mt-2 text-sm text-stone-700 font-sans">
          You're currently signed in as <span className="font-semibold">{user.role}</span>. To apply, switch your role to Driver from your dashboard or role selection screen.
        </p>
        <Link to="/role-select">
          <Button data-testid="drivers-app-switch-role" className="mt-5 bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-6 py-5 rounded-md">
            Choose Driver Role
          </Button>
        </Link>
      </div>
    );
  }

  // Already submitted
  if (existing?.status === "submitted") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-8" data-testid="drivers-app-submitted">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-emerald-700" size={28} strokeWidth={1.75} />
          <h3 className="font-serif text-2xl text-emerald-900">Application received</h3>
        </div>
        <p className="mt-3 text-sm text-stone-700 font-sans">
          Thanks {user.name}. We have your application on file. {isVerified
            ? "Your ID is on file and you're verified."
            : "Please upload your driver's license below to complete verification."}
        </p>
        {!isVerified && (
          <div className="mt-5">
            <Label htmlFor="id-upload-2" className="text-sm font-semibold text-stone-800">Upload Driver's License / Government ID</Label>
            <input
              id="id-upload-2"
              data-testid="drivers-id-upload-2"
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              onChange={onUpload}
              disabled={uploading}
              className="mt-2 block w-full text-sm text-stone-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-emerald-800 file:text-stone-50 hover:file:bg-emerald-700"
            />
          </div>
        )}
        <Link to="/dashboard">
          <Button data-testid="drivers-app-go-dash" className="mt-6 bg-stone-900 hover:bg-stone-800 text-stone-50 px-6 py-5 rounded-md">
            Go to Driver Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  // The actual form
  return (
    <form onSubmit={submit} data-testid="drivers-application-form" className="bg-white border border-stone-200 rounded-md p-8 sm:p-10 space-y-7">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="full_name" className="text-sm font-semibold text-stone-800">Full legal name</Label>
          <Input id="full_name" data-testid="drv-full-name" required value={form.full_name} onChange={set("full_name")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-sm font-semibold text-stone-800">Phone</Label>
          <Input id="phone" data-testid="drv-phone" required value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className="mt-2" />
        </div>
        <div>
          <Label htmlFor="license_number" className="text-sm font-semibold text-stone-800">Driver's license number</Label>
          <Input id="license_number" data-testid="drv-license" required value={form.license_number} onChange={set("license_number")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="license_state" className="text-sm font-semibold text-stone-800">Issuing state</Label>
          <Input id="license_state" data-testid="drv-license-state" required value={form.license_state} onChange={set("license_state")} placeholder="e.g., TX" className="mt-2" />
        </div>
        <div>
          <Label htmlFor="vehicle_make" className="text-sm font-semibold text-stone-800">Vehicle make</Label>
          <Input id="vehicle_make" data-testid="drv-make" required value={form.vehicle_make} onChange={set("vehicle_make")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="vehicle_model" className="text-sm font-semibold text-stone-800">Vehicle model</Label>
          <Input id="vehicle_model" data-testid="drv-model" required value={form.vehicle_model} onChange={set("vehicle_model")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="vehicle_year" className="text-sm font-semibold text-stone-800">Vehicle year</Label>
          <Input id="vehicle_year" data-testid="drv-year" required value={form.vehicle_year} onChange={set("vehicle_year")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="license_plate" className="text-sm font-semibold text-stone-800">License plate</Label>
          <Input id="license_plate" data-testid="drv-plate" required value={form.license_plate} onChange={set("license_plate")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="years_driving" className="text-sm font-semibold text-stone-800">Years driving</Label>
          <Input id="years_driving" data-testid="drv-years" value={form.years_driving} onChange={set("years_driving")} className="mt-2" />
        </div>
        <div className="hidden sm:block" />
        <div>
          <Label htmlFor="payout_paypal" className="text-sm font-semibold text-stone-800">PayPal email (for payouts)</Label>
          <Input id="payout_paypal" data-testid="drv-paypal" value={form.payout_paypal} onChange={set("payout_paypal")} className="mt-2" />
        </div>
        <div>
          <Label htmlFor="payout_cashapp" className="text-sm font-semibold text-stone-800">CashApp $tag (for payouts)</Label>
          <Input id="payout_cashapp" data-testid="drv-cashapp" value={form.payout_cashapp} onChange={set("payout_cashapp")} placeholder="$yourtag" className="mt-2" />
        </div>
      </div>

      <div className="border-t border-stone-200 pt-6">
        <Label htmlFor="id-upload" className="text-sm font-semibold text-stone-800 flex items-center gap-2">
          <Upload size={16} /> Upload Driver's License / Government ID
        </Label>
        <p className="text-xs text-stone-500 mt-1 font-sans">JPG, PNG, WEBP or PDF — max 8 MB. Stored privately and only viewable by admins.</p>
        <input
          id="id-upload"
          data-testid="drivers-id-upload"
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={onUpload}
          disabled={uploading}
          className="mt-3 block w-full text-sm text-stone-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-emerald-800 file:text-stone-50 hover:file:bg-emerald-700"
        />
        {isVerified && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-800 font-semibold">
            <CheckCircle2 size={16} /> ID on file — you're verified.
          </div>
        )}
      </div>

      <div className="border-t border-stone-200 pt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            data-testid="drv-agree-rules"
            checked={form.agreed_to_rules}
            onCheckedChange={(v) => setForm((f) => ({ ...f, agreed_to_rules: !!v }))}
            className="mt-1"
          />
          <span className="text-sm text-stone-700 font-sans leading-relaxed">
            I have read and agree to follow the <span className="font-semibold text-emerald-800">Azure Gypsy Rules of Engagement</span> shown above — including respecting hobby farmers, never eating their produce, not selling our service, and being kind to everyone I encounter on the route.
          </span>
        </label>
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={submitting || !form.agreed_to_rules}
          data-testid="drivers-app-submit"
          className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-8 py-6 rounded-md disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit Application"}
        </Button>
      </div>
    </form>
  );
}

export default function Drivers() {
  return (
    <div>
      <Seo
        url="/drivers"
        title="Drive With Us"
        description="Now hiring delivery drivers in every state. Pick up from local hobby farmers, deliver to clients in your community. 100% of tips, PayPal & CashApp payouts."
        image="https://azuregypsy.org/og-drivers.jpg"
      />
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1563558055993-34e0a8d9322c?w=1600&q=80"
            alt="Local delivery driver"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/55 via-stone-900/30 to-stone-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-28 pb-32 sm:pt-36 sm:pb-40">
          <div className="text-[11px] uppercase tracking-[0.4em] text-amber-200 font-sans">Now Hiring · Every State</div>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl text-stone-50 leading-[1.05] max-w-3xl">
            Drive for the hobby farmers in your community.
          </h1>
          <p className="mt-6 font-sans text-stone-100/90 text-lg max-w-2xl">
            Pick up fresh produce, eggs, meats and barter goods from local hobby farmers — drop off to clients across your state. Local routes. Honest pay. Real food.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#apply">
              <Button data-testid="drivers-signup-btn" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">
                Apply to Drive
              </Button>
            </a>
            <Link to="/contact">
              <Button data-testid="drivers-questions-btn" variant="outline" className="bg-white/10 backdrop-blur border-stone-50/40 text-stone-50 hover:bg-white/20 px-7 py-6 rounded-md">
                I Have Questions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">What You Get</div>
        <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-stone-900">Honest pay. Honest work.</h2>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Item icon={DollarSign} title="Competitive pay" text="Paid for every route you complete. Pay rates are shared in your driver dashboard after you're approved." />
          <Item icon={HandCoins} title="100% of tips" text="Tips paid by the client go straight to you. We never take a cut." />
          <Item icon={Truck} title="Flexible hours" text="Accept the pickups you want, when you want. No quotas, no schedule." />
          <Item icon={Clock4} title="Quick onboarding" text="Sign up with Google, pick the driver role, you're in. No phone interviews." />
          <Item icon={MapPin} title="Local routes only" text="State-level pickup zones. No cross-country runs. Know your community." />
          <Item icon={ShieldCheck} title="PayPal & CashApp payouts" text="Pick how you cash out. Add your handle in your driver dashboard." />
        </div>
      </section>

      {/* RULES OF ENGAGEMENT */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-20">
        <div className="bg-stone-900 rounded-md p-10 sm:p-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Rules of Engagement</div>
          <h3 className="mt-3 font-serif text-3xl sm:text-4xl text-stone-50 leading-tight">The Azure Gypsy code.</h3>
          <p className="mt-3 text-stone-300 font-sans max-w-2xl text-sm">
            Every driver who joins Azure Gypsy Farms commits to a small set of non-negotiables. These keep our hobby farmers safe, our clients happy, and our community kind.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-8 max-w-5xl">
            <Rule
              icon={Sprout}
              title="Work with the hobby farmers — not around them."
              text="Coordinate pickups, respect their land, and follow their handling instructions. They grew it; we just carry it."
            />
            <Rule
              icon={FileCheck2}
              title="Follow the Azure Gypsy rules."
              text="Stick to assigned routes, use the in-app status updates, and never share client or farmer contact info outside the platform."
            />
            <Rule
              icon={Sparkles}
              title="Do not eat the farmers' produce."
              text="What you're carrying belongs to a client. If a hobby farmer offers you food as a gift, that's between you and them — never help yourself."
            />
            <Rule
              icon={Lock}
              title="Do not sell our service."
              text="No upselling, side-deals, or off-platform delivery contracts using Azure Gypsy connections. This protects everyone involved."
            />
            <Rule
              icon={Heart}
              title="Be kind to others."
              text="To farmers, to clients, to strangers at the gate. Calm tone, clean truck, friendly handoff. Kindness is the brand."
            />
            <Rule
              icon={ShieldCheck}
              title="Verified identity, always."
              text="Upload your driver's license at sign-up. We verify every driver to keep our network of hobby farmers and clients safe."
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-20">
        <div className="bg-stone-900 rounded-md p-10 sm:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">How It Works</div>
            <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-stone-50 leading-tight">From sign-up to first delivery, in a day.</h3>
            <ol className="mt-8 space-y-5">
              {[
                ["Sign in with Google", "Click Apply to Drive and use your Google account. Takes 30 seconds."],
                ["Pick the Driver role + your state", "We use your state to surface only nearby pickups."],
                ["Upload your driver's license", "Required for verification. Stored privately, admins only."],
                ["Add payout info", "PayPal email or CashApp $tag, in your driver dashboard."],
                ["Accept your first pickup", "Browse 'Available Pickups' in your dashboard. Hit accept."],
                ["Deliver & get paid", "Mark statuses in real time. Earnings post to your ledger instantly."],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-5">
                  <span className="w-8 h-8 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-serif text-base shrink-0">{i + 1}</span>
                  <div>
                    <div className="font-serif text-lg text-stone-50">{t}</div>
                    <div className="text-sm text-stone-300 font-sans mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-stone-800 border border-stone-700 rounded-md p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">What Drivers Love</div>
            <ul className="mt-6 space-y-4 font-sans text-stone-300 text-sm">
              <li className="flex gap-3"><span className="text-amber-300">·</span> 100% of every tip lands in your pocket</li>
              <li className="flex gap-3"><span className="text-amber-300">·</span> Local pickup zones — never far from home</li>
              <li className="flex gap-3"><span className="text-amber-300">·</span> Real-time GPS handoff so clients know you're coming</li>
              <li className="flex gap-3"><span className="text-amber-300">·</span> Cash out via PayPal or CashApp, your choice</li>
              <li className="flex gap-3"><span className="text-amber-300">·</span> Hobby farmers can gift you free food, with respect</li>
            </ul>
            <div className="mt-6 text-xs text-stone-400 font-sans">Detailed pay structure is shared in your private driver dashboard after you sign up.</div>
          </div>
        </div>
      </section>

      {/* DRIVER APPLICATION FORM */}
      <section id="apply" className="max-w-4xl mx-auto px-6 sm:px-12 pb-28 scroll-mt-24">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Driver Application</div>
        <h3 className="mt-3 font-serif text-4xl sm:text-5xl text-stone-900">Apply to drive.</h3>
        <p className="mt-3 font-sans text-stone-600 max-w-2xl">
          Fill out your driver profile, upload your driver's license, and agree to the Azure Gypsy Rules of Engagement. We'll review and email you within 48 hours.
        </p>
        <div className="mt-8">
          <DriverApplicationForm />
        </div>
      </section>
    </div>
  );
}
